import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Home as HomeIcon } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import RoomVisualizer from "@/components/RoomVisualizer";
import { getArtwork, getArtworks } from "@/lib/api";
import { TID } from "@/constants/testIds";

export default function ArtworkDetail() {
  const { id } = useParams();
  const [artwork, setArtwork] = useState(null);
  const [related, setRelated] = useState([]);
  const [visOpen, setVisOpen] = useState(false);

  useEffect(() => {
    getArtwork(id).then(setArtwork).catch(() => setArtwork(null));
  }, [id]);

  useEffect(() => {
    if (!artwork) return;
    getArtworks({ category: artwork.category })
      .then((rows) => setRelated(rows.filter((r) => r.id !== artwork.id).slice(0, 3)))
      .catch(() => {});
  }, [artwork]);

  if (!artwork) {
    return (
      <section className="py-32 text-center">
        <p className="catalog-label">Loading…</p>
      </section>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "VisualArtwork",
    name: artwork.title,
    creator: { "@type": "Person", name: "Colin Darke" },
    artMedium: artwork.medium,
    artform: artwork.category,
    dateCreated: String(artwork.year),
    image: artwork.image_url,
    description: artwork.description,
    width: artwork.dimensions,
    artworkSurface: artwork.medium,
  };

  return (
    <>
      <SEOHead
        title={`${artwork.title} (${artwork.year}) — Colin Darke`}
        description={`${artwork.title} by Colin Darke — ${artwork.medium}, ${artwork.dimensions}. ${artwork.description}`}
        path={`/artwork/${artwork.id}`}
        image={artwork.image_url}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <section className="pt-8 pb-4">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <nav className="catalog-label text-[hsl(var(--ink-muted))]">
            <Link to="/artwork" className="hover:text-[hsl(var(--vermillion))]">
              Archive
            </Link>{" "}
            / <span>{artwork.category}</span>
          </nav>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="bg-[hsl(var(--bg-secondary))] flex items-center justify-center p-4 sm:p-8">
              <img
                src={artwork.image_url}
                alt={`${artwork.title} — ${artwork.medium} by Colin Darke, ${artwork.year}.`}
                className="max-w-full max-h-[80vh] w-auto h-auto object-contain"
              />
            </div>
          </div>
          <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <p className="catalog-label">
              {artwork.category} · {artwork.year}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl mt-2 leading-tight">
              {artwork.title}
            </h1>
            <div className="mt-6 space-y-2 catalog-label text-[hsl(var(--ink))]">
              <p><span className="text-[hsl(var(--ink-muted))]">Artist</span> · Colin Darke</p>
              <p><span className="text-[hsl(var(--ink-muted))]">Medium</span> · {artwork.medium}</p>
              <p><span className="text-[hsl(var(--ink-muted))]">Dimensions</span> · {artwork.dimensions}</p>
              <p><span className="text-[hsl(var(--ink-muted))]">Status</span> · <span className="text-[hsl(var(--vermillion))]">{artwork.availability}</span></p>
            </div>
            <p className="mt-6 text-[hsl(var(--ink-muted))] leading-relaxed">
              {artwork.description}
            </p>

            <div className="mt-8 flex flex-col gap-3">
              <button
                onClick={() => setVisOpen(true)}
                data-testid={TID.seeInHomeBtn}
                className="inline-flex items-center justify-center gap-2 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] py-3 px-5 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--vermillion))] transition-colors"
              >
                <HomeIcon size={14} /> See it in your home
              </button>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-[hsl(var(--ink))] py-3 px-5 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors"
              >
                Inquire about acquisition <ArrowUpRight size={14} />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-[hsl(var(--bg-secondary))] border-t border-[hsl(var(--line))]">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
            <p className="catalog-label mb-4">Related work</p>
            <h2 className="font-serif text-3xl sm:text-4xl mb-8">
              More from {artwork.category}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-14">
              {related.map((a) => (
                <Link
                  key={a.id}
                  to={`/artwork/${a.id}`}
                  className="group block"
                >
                  <div
                    className="w-full bg-[hsl(var(--bg))] flex items-center justify-center overflow-hidden"
                    style={{ aspectRatio: "4/5" }}
                  >
                    <img
                      src={a.image_url}
                      alt={a.title}
                      className="max-w-full max-h-full object-contain group-hover:scale-[1.02] transition-transform duration-700"
                    />
                  </div>
                  <p className="catalog-label mt-3">
                    {a.medium} · {a.year}
                  </p>
                  <h3 className="font-serif text-xl mt-1">{a.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <RoomVisualizer open={visOpen} onOpenChange={setVisOpen} artwork={artwork} />
    </>
  );
}
